import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	MenuItem,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

/** NGER readiness report: thresholds, facility summary, energy component. */
const openDownload = (url: string) => {
	// Anchor navigation instead of window.open: the PDF takes seconds to
	// render and a delayed window.open gets popup-blocked.
	const link = document.createElement("a");
	link.href = url;
	link.rel = "noopener";
	document.body.appendChild(link);
	link.click();
	link.remove();
};

const NgerReport: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const currentYear = new Date().getFullYear();
	const [year, setYear] = useState<number>(currentYear - 1);
	const [busy, setBusy] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	const downloadHtml = async () => {
		setBusy("html");
		setError(null);
		try {
			const token = await getAccessTokenSilently();
			const res = await fetch(`${API}/api/nger-report?year=${year}`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			if (!res.ok) {
				const data = await res.json().catch(() => ({}));
				setError(
					data.Message || "Could not generate the report. Try again.",
				);
				return;
			}
			const blob = await res.blob();
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `nger_readiness_${year}.html`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			setError("Could not generate the report. Check your connection.");
		} finally {
			setBusy(null);
		}
	};

	const downloadPdf = async () => {
		setBusy("pdf");
		setError(null);
		try {
			const token = await getAccessTokenSilently();
			const res = await fetch(`${API}/api/nger-report/pdf?year=${year}`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			const json = await res.json().catch(() => ({}));
			if (!res.ok || !json.Ok || !json.Data?.url)
				throw new Error(json.Message || "Could not generate the PDF");
			openDownload(json.Data.url);
		} catch (e: any) {
			setError(e.message || "Could not generate the PDF");
		} finally {
			setBusy(null);
		}
	};

	const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

	return (
		<Box display="flex" justifyContent="center" padding={3}>
			<Paper
				elevation={0}
				sx={{
					maxWidth: 620,
					width: "100%",
					padding: "32px",
					border: "1px solid rgba(0, 23, 28, 0.10)",
					borderRadius: "12px",
				}}
			>
				<Typography variant="h5" gutterBottom>
					NGER report
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ marginBottom: 3 }}
				>
					An NGER readiness assessment for a reporting year: your
					facility and corporate Scope 1 + 2 totals against the
					National Greenhouse and Energy Reporting thresholds (25 kt
					per facility, 50 kt corporate, and the 100 kt Safeguard
					Mechanism baseline), an emissions-by-source breakdown, and
					the electricity component of the energy thresholds. An
					internal readiness document — not an NGER lodgement.
				</Typography>

				<Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
					<TextField
						select
						label="Reporting year"
						value={year}
						onChange={(e) => setYear(Number(e.target.value))}
						size="small"
						sx={{ minWidth: 150 }}
					>
						{years.map((y) => (
							<MenuItem key={y} value={y}>
								{y}
							</MenuItem>
						))}
					</TextField>
					<Button
						variant="contained"
						color="secondary"
						startIcon={
							busy === "html" ? (
								<CircularProgress size={16} color="inherit" />
							) : (
								<DescriptionOutlinedIcon />
							)
						}
						disabled={busy !== null}
						onClick={downloadHtml}
					>
						{busy === "html" ? "Generating…" : "NGER report"}
					</Button>
					<Button
						variant="outlined"
						color="secondary"
						startIcon={
							busy === "pdf" ? (
								<CircularProgress size="1rem" />
							) : (
								<PictureAsPdfOutlinedIcon />
							)
						}
						disabled={busy !== null}
						onClick={downloadPdf}
					>
						{busy === "pdf" ? "Rendering…" : "NGER report (PDF)"}
					</Button>
				</Box>

				{error && (
					<Alert severity="info" sx={{ marginTop: 3 }}>
						{error}
					</Alert>
				)}

				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ marginTop: 3 }}
				>
					Figures are calendar-year; NGER reports the 1 July – 30 June
					year. Fuel energy content is not yet assessed, so the energy
					section covers electricity only.
				</Typography>
			</Paper>
		</Box>
	);
};

export default NgerReport;
